import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { Add } from '@mui/icons-material';
import Switch from '@mui/material/Switch';

import Button from '../../common/Button';
import { type TypeProject } from '../../common/dataTypes';
import { useGetProjects } from '../../hooks';
import {
  isOrgAdmin,
  useGetRouteParams,
  useGetSearchResults,
} from '../../hooks/tanstack/useUtilsHook';
import useProjectStore from '../../hooks/zustand/useProjectStore';
import { useCurrentMembership } from '../../utils/helpers';
import SearchInput from '../SearchInput';

import ProjectsTable from './ProjectsTable';

const ProjectsView = () => {
  const membership = useCurrentMembership();
  const { orgId } = useGetRouteParams();

  const {
    data: projects,
    isRefetching: isRefetchingProjects,
    isLoading: isLoadingProjects,
  } = useGetProjects(
    {
      orgId,
    },
    {
      enabled: !!orgId,
    }
  );

  const isHidingUnpinnedProjects = useProjectStore(
    (state) => state.isHidingUnpinnedProjects
  );

  const updateIsHidingUnpinnedProjects = useProjectStore(
    (state) => state.updateIsHidingUnpinnedProjects
  );

  const form = useForm();

  const { watch } = form;

  const navigate = useNavigate();

  const handleOnClick = () => {
    navigate(`/${orgId}/projects/create`);
  };

  const handleToggleHidingUnpinnedProjects = () => {
    updateIsHidingUnpinnedProjects(!isHidingUnpinnedProjects);
  };

  const searchResults = useGetSearchResults<TypeProject>({
    searchValue: watch('search'),
    data: projects,
  });

  return (
    <div className="mx-auto w-full max-w-lg 3xl:max-w-2xl">
      <FormProvider {...form}>
        <div className="flex flex-col gap-6 py-6">
          <h1 className="text-2xl font-bold">Projects</h1>
          <div className="flex items-center justify-between">
            <form className="flex items-center gap-3">
              <div className="min-w-[300px]">
                <SearchInput />
              </div>
              <div className="flex items-center">
                <Switch
                  checked={isHidingUnpinnedProjects}
                  onChange={handleToggleHidingUnpinnedProjects}
                  inputProps={{ 'aria-label': 'Switch hide unpinned projects' }}
                />
                <p className=" text-gray-500">Pinned projects</p>
              </div>
            </form>
            {isOrgAdmin(membership?.organization?.role) && (
              <Button variant="contained_red" onClick={handleOnClick}>
                <Add
                  style={{
                    height: '22px',
                    width: '22px',
                  }}
                />
                New Project
              </Button>
            )}
          </div>
          <ProjectsTable
            defaultProjects={searchResults}
            isHidingUnpinnedProjects={isHidingUnpinnedProjects}
            isLoading={isLoadingProjects || isRefetchingProjects}
          />
        </div>
      </FormProvider>
    </div>
  );
};

export default ProjectsView;
